import Navigate, { NavigateProps } from "common/router/Navigate";
import LinkButton, { LinkButtonProps } from "common/components/LinkButton";

import Page from "client/common/components/layouts/Page";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import StreamlinedHeader from "client/common/components/header/StreamlinedHeader";
import { urls } from "client/routes";
import { useShippingInfoDrawer } from "client/common/partners/stackables";
import { useSupportChat } from "client/support/hooks";

import styles from "../styles/CheckoutPage.module.less";

type CheckoutPageHeaderProps = {
  onPrevious?: () => void;
};

const CheckoutPageHeader = ({ onPrevious }: CheckoutPageHeaderProps) => {
  const chat = useSupportChat();

  return <StreamlinedHeader onPrevious={onPrevious} onHelp={() => chat.show()} />;
};

type CheckoutPageProps = CheckoutPageHeaderProps & {
  children?: React.ReactNode;
  sider?: React.ReactNode;
  navigation?: React.ReactNode;
};

const CheckoutPage = ({ children, sider, navigation, ...headerProps }: CheckoutPageProps) => {
  const { open: openShippingInfoDrawer } = useShippingInfoDrawer();
  const legalLinks: (LinkButtonProps & { children: string })[] = [
    {
      children: "Shipping Policy",
      onClick: openShippingInfoDrawer,
    },
    {
      children: "FAQ",
      dest: urls.support.helpCenter(),
    },
    {
      children: "Terms of Use",
      dest: urls.support.termsOfUse(),
    },
    {
      children: "Privacy Policy",
      dest: urls.support.privacyPolicy(),
    },
  ];

  return (
    <Page Header={<CheckoutPageHeader {...headerProps} />} Footer={null}>
      <div className={styles.page}>
        <div className={styles.main}>
          <div className={styles.navigation}>{navigation}</div>
          <div className={styles.content}>{children}</div>
          <div className={styles.footer}>
            {legalLinks.map(({ children: label, ...other }) => (
              <LinkButton className={styles.link} key={label} {...other}>
                {label}
              </LinkButton>
            ))}
          </div>
        </div>
        <div className={styles.background} />
        <div className={styles.sider}>{sider}</div>
      </div>
    </Page>
  );
};

const LoadingCheckoutPage = () => {
  return (
    <CheckoutPage>
      <LoadingContainer />
    </CheckoutPage>
  );
};

type RedirectInvalidCheckoutPageProps = NavigateProps;

const RedirectInvalidCheckoutPage = (props: RedirectInvalidCheckoutPageProps) => {
  return (
    <CheckoutPage>
      <Navigate {...props} />
    </CheckoutPage>
  );
};

export default Object.assign(CheckoutPage, {
  Loading: LoadingCheckoutPage,
  RedirectInvalid: RedirectInvalidCheckoutPage,
});
